// Tracker pins — the Position Tracker's thumbtack, per signed-in user (#943).
//
// A pin is a watch copy in the tracker's Pinned strip (#188/#339). A pinned
// KALSHI resting order is also protected: Cancel All, Cancel visible, Cancel
// this game and Pull All subtract it at freeze time (freezeCancelSnapshot).
// Presentation state only — the relay never sees a pin and cancels exactly
// what it is asked to cancel.
//
// ── Persistence ─────────────────────────────────────────────────────────
// One localStorage key per user, same contract as savedGames: logout clears
// memory (authStore.purgeUserCaches → resetTrackerPins) but leaves the stored
// list, so signing back in restores it; another user never sees it. Other
// tabs of the same user re-read the key on every write (storage listener
// below), so a pin set in one tab protects the order in all of them. The
// legacy browser-wide list (`g1mt_tracker_pins`) had no owner and only ever
// meant "watch": its position and Polymarket pins are adopted by the first
// user who hydrates with no list of their own, its Kalshi ORDER pins are
// dropped (nobody inherits a protection they never set), and it is removed.
//
// ── No auto-unpin ───────────────────────────────────────────────────────
// A pin is removed only when the trader unpins it (or an edit moves it to the
// replacement order). A Kalshi order pin whose order is no longer listed stays
// and renders as a tombstone in the Pinned strip. Kalshi's resting list can
// omit a live order for a while (org-key filtering, relisted tickers — see
// relay kalshiOrderRecovery), and a pin that expired during that gap would
// come back unprotected. Venue ids are never reused, so a pin on a gone order
// protects nothing and costs nothing.

import { create } from 'zustand';
import { storageKey } from '@/lib/infra/storage';
import { ownedBy } from './gameMarkIdentity';
import {
  isKalshiOrderPin,
  orderPinKey,
  runOrderEditWithPinTransfer,
  transferOrderPin,
  type OrderVenue,
  type PinnedEntry,
} from '@/views/PositionTracker/pinIdentity';
import {
  clearLegacyPinnedEntries,
  isPinnedEntry,
  readPinnedEntries,
} from '@/views/PositionTracker/pinsPersistence';

export type { PinnedEntry } from '@/views/PositionTracker/pinIdentity';

interface TrackerPinsState {
  /** Whose pins are loaded. Null when signed out. */
  userId: string | null;
  /** Ordered by pin time (append). */
  entries: PinnedEntry[];
  keys: ReadonlySet<string>;

  hydrateFor: (userId: string | null) => void;
  /** Re-read the loaded user's stored list (another tab wrote it). */
  reload: () => void;
  toggle: (entry: PinnedEntry, viewerId: string | null) => void;
  transfer: (previousKey: string, replacementKey: string, viewerId: string | null) => void;
}

const NO_ENTRIES: PinnedEntry[] = [];
const NO_KEYS: ReadonlySet<string> = new Set<string>();

export function selectOwnedPins(
  state: { userId: string | null; entries: PinnedEntry[] },
  viewerId: string | null,
): PinnedEntry[] {
  return ownedBy(state, viewerId) ? state.entries : NO_ENTRIES;
}

export function selectOwnedPinKeys(
  state: { userId: string | null; keys: ReadonlySet<string> },
  viewerId: string | null,
): ReadonlySet<string> {
  return ownedBy(state, viewerId) ? state.keys : NO_KEYS;
}

/** Venue ids of the viewer's pinned Kalshi orders — what bulk cancels skip. */
export function pinnedKalshiOrderIds(
  state: { userId: string | null; entries: PinnedEntry[] },
  viewerId: string | null,
): ReadonlySet<string> {
  return new Set(selectOwnedPins(state, viewerId).filter(isKalshiOrderPin).map((e) => e.key));
}

/** Clear the in-memory pins. The stored list is kept; see file header. */
export function resetTrackerPins(): void {
  useTrackerPins.getState().hydrateFor(null);
}

/** Run an order edit from any surface and keep the order's pin on it. A
 *  cancel+place edit mints a new id; the pin follows only once the venue
 *  confirmed the replacement (see runOrderEditWithPinTransfer), so a failed
 *  edit leaves the pin where it was. Every edit surface goes through here —
 *  an edit that skipped it would leave the replacement unpinned and the next
 *  Pull All would cancel it. */
export function editOrderKeepingPin(
  venue: OrderVenue,
  previousOrderId: string,
  edit: () => Promise<{ orderId: string }>,
  viewerId: string | null,
): Promise<{ orderId: string }> {
  return runOrderEditWithPinTransfer(previousOrderId, edit, (prevId, nextId) => {
    useTrackerPins
      .getState()
      .transfer(orderPinKey(venue, prevId), orderPinKey(venue, nextId), viewerId);
  });
}

function storageKeyFor(userId: string): string {
  return storageKey(`galactus:trackerPins:${userId}`);
}

/** Null when the user has no stored list at all (as opposed to an empty
 *  one) — only then is the legacy browser-wide list adopted. */
function load(userId: string): PinnedEntry[] | null {
  try {
    const raw = localStorage.getItem(storageKeyFor(userId));
    if (raw == null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return [];
    const { entries } = parsed as Record<string, unknown>;
    // Per-entry validation: one bad entry is dropped, its siblings survive.
    return Array.isArray(entries) ? entries.filter(isPinnedEntry) : [];
  } catch {
    return [];
  }
}

/** Legacy browser-wide pins had no owner and only meant "watch". Keep the
 *  watch-only kinds; never promote an ownerless Kalshi order pin into a
 *  protection for whoever signs in first. */
function adoptableLegacyPins(): PinnedEntry[] {
  return readPinnedEntries().filter((e) => !isKalshiOrderPin(e));
}

function persist(userId: string, entries: PinnedEntry[]): void {
  try {
    localStorage.setItem(storageKeyFor(userId), JSON.stringify({ entries }));
  } catch {
    // Full / unavailable storage — pins still work in memory this session.
  }
}

function keysOf(entries: PinnedEntry[]): ReadonlySet<string> {
  return new Set(entries.map((e) => e.key));
}

export const useTrackerPins = create<TrackerPinsState>((set, get) => {
  const commit = (entries: PinnedEntry[]) => {
    const { userId } = get();
    if (!userId) return;
    set({ entries, keys: keysOf(entries) });
    persist(userId, entries);
  };

  return {
    userId: null,
    entries: NO_ENTRIES,
    keys: NO_KEYS,

    hydrateFor: (userId) => {
      if (get().userId === userId) return;
      if (!userId) {
        set({ userId: null, entries: NO_ENTRIES, keys: NO_KEYS });
        return;
      }
      let entries = load(userId);
      if (entries == null) {
        entries = adoptableLegacyPins();
        if (entries.length > 0) persist(userId, entries);
        clearLegacyPinnedEntries();
      }
      set({ userId, entries, keys: keysOf(entries) });
    },

    reload: () => {
      const { userId } = get();
      if (!userId) return;
      const entries = load(userId) ?? [];
      set({ entries, keys: keysOf(entries) });
    },

    // Both writers start from the STORED list, not this tab's copy: another tab
    // of the same user may have written a pin whose storage event has not
    // reached us yet, and writing our stale copy back would silently unpin it
    // (and the next sweep would cancel that order).
    toggle: (entry, viewerId) => {
      const state = get();
      if (!state.userId || !ownedBy(state, viewerId)) return;
      const current = load(state.userId) ?? state.entries;
      const pinned = current.some((e) => e.key === entry.key);
      commit(pinned ? current.filter((e) => e.key !== entry.key) : [...current, entry]);
    },

    transfer: (previousKey, replacementKey, viewerId) => {
      const state = get();
      if (!state.userId || !ownedBy(state, viewerId)) return;
      const current = load(state.userId) ?? state.entries;
      const entries = transferOrderPin(current, previousKey, replacementKey);
      if (entries === current) return;
      commit(entries);
    },
  };
});

/** #943 review — other tabs of the same user. The `storage` event fires only
 *  in OTHER tabs, so this cannot loop; the key check keeps unrelated writes
 *  from re-rendering the strip (same shape as liveHotkeys). */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    const { userId } = useTrackerPins.getState();
    if (!userId || e.key !== storageKeyFor(userId)) return;
    useTrackerPins.getState().reload();
  });
}
